/** Labor minute fields on price items / quote line items */
export const LABOR_MINUTE_FIELDS = [
  'sn_hours_day',
  'sn_hours_night',
  'ena_hours_day',
  'ena_hours_night',
  'cw_hours_day',
  'cw_hours_night',
];

export function hasLaborMinutes(item) {
  if (!item) return false;
  if (LABOR_MINUTE_FIELDS.some((field) => parseFloat(item[field] || 0) > 0)) {
    return true;
  }
  const defaults = item.metadata?.catalogue?.defaults;
  if (defaults) {
    return LABOR_MINUTE_FIELDS.some((field) => parseFloat(defaults[field] || 0) > 0);
  }
  return false;
}

export function isZeroPrice(value) {
  if (value === null || value === undefined || value === '') return false;
  const parsed = parseFloat(value);
  return !Number.isNaN(parsed) && parsed === 0;
}

/** Preserve intentional 0 — never use `value || 1` for multipliers. */
export function resolveMultiplier(multiplier, defaultValue = 1) {
  if (multiplier === null || multiplier === undefined || multiplier === '') {
    return defaultValue;
  }
  const parsed = parseFloat(multiplier);
  return Number.isNaN(parsed) ? defaultValue : parsed;
}

export function getZeroPriceMessage({ hasLabor } = {}) {
  if (hasLabor) {
    return 'Price is R0 but this item includes labour minutes.';
  }
  return 'Price is R0.';
}

export function getZeroMultiplierMessage({ context } = {}) {
  if (context === 'careplan') {
    return 'Multiplier is 0 — this activity contributes no hours.';
  }
  return 'Multiplier is 0 — this line totals R0.';
}

export function summarizeZeroMultiplierWarnings(lines) {
  let zeroMultiplier = 0;
  (lines || []).forEach((line) => {
    if (isZeroPrice(resolveMultiplier(line?.multiplier))) {
      zeroMultiplier += 1;
    }
  });
  if (zeroMultiplier === 0) return null;
  return zeroMultiplier === 1
    ? '1 activity has multiplier set to 0 and contributes no hours.'
    : `${zeroMultiplier} activities have multiplier set to 0 and contribute no hours.`;
}

export function summarizeZeroLineWarnings(lines) {
  let zeroWithLabor = 0;
  let zeroOnly = 0;
  let zeroMultiplier = 0;

  (lines || []).forEach((line) => {
    const unit = parseFloat(line.unitPrice ?? line.price ?? 0);
    const multRaw = line.multiplier;
    const safeMult = resolveMultiplier(multRaw);
    const lineTotal = (Number.isNaN(unit) ? 0 : unit) * safeMult;
    const labor = hasLaborMinutes(line);

    if (isZeroPrice(safeMult)) {
      zeroMultiplier += 1;
    }
    if (!Number.isNaN(lineTotal) && lineTotal === 0) {
      if (labor) zeroWithLabor += 1;
      else zeroOnly += 1;
    }
  });

  if (zeroWithLabor === 0 && zeroOnly === 0 && zeroMultiplier === 0) {
    return null;
  }

  const parts = [];
  if (zeroWithLabor > 0) {
    parts.push(
      zeroWithLabor === 1
        ? '1 line totals R0 and includes labour'
        : `${zeroWithLabor} lines total R0 and include labour`
    );
  }
  if (zeroOnly > 0) {
    parts.push(
      zeroOnly === 1 ? '1 line totals R0' : `${zeroOnly} lines total R0`
    );
  }
  if (zeroMultiplier > 0 && zeroWithLabor === 0 && zeroOnly === 0) {
    parts.push(
      zeroMultiplier === 1
        ? '1 line has multiplier set to 0'
        : `${zeroMultiplier} lines have multiplier set to 0`
    );
  } else if (zeroMultiplier > 0 && (zeroWithLabor > 0 || zeroOnly > 0)) {
    // Multiplier zeros are already covered by line totals; only add if useful
  }

  return `One or more lines need attention: ${parts.join('; ')}.`;
}
